/**
 * Runner job readiness for the Fleet pages, from the runner's own status
 * payload as passed through by the broker (private/auth fleetHealth.ts,
 * `runner:<id>` entity detail).
 *
 * Primary signal: `admission` ({state: starting|serving|rolling-out|degraded,
 * reason}, internal/runner jobAdmission.ts). Intake is open ONLY in
 * `serving`, so "accepting jobs" is shown only then — `occupancy: "ready"`
 * alone said "ready for jobs" while intake was closed for a rollout.
 * `rollout` (internal/runner rolloutProgress.ts) adds the phase, target
 * version and warm buckets done/total while a rollout runs.
 *
 * 2026-10-07 (pure-runner-1): each warm bucket may carry `status: "failed"`
 * + `lastFailure` {ts, exitCode, reason: oom|compile-error|timeout|other,
 * errorLines}; `warmTargetSets` holds the live and the staged version's
 * buckets explicitly (staged = the rollout's target, or a failed one that
 * is backing off), and `rolloutBackoff` says when a failed version is
 * retried. A staged rollout that failed is therefore visible even after it
 * ended and intake reopened on the live pair.
 *
 * Returns null for an older runner build without `admission`; callers then
 * keep their previous occupancy-based rendering.
 */

export type AdmissionState =
  "starting" | "serving" | "rolling-out" | "degraded";

export type ReadinessKind =
  "accepting" | "processing" | "rolling-out" | "starting" | "degraded";

export type BucketStatus = "warm" | "prewarming" | "pending" | "failed";

export interface BucketFailure {
  version: string | null;
  ts: number | null;
  exitCode: number | null;
  /** oom | compile-error | timeout | other */
  reason: string;
  errorLines: string[];
}

export interface RolloutBucket {
  label: string;
  status: BucketStatus;
  lastFailure: BucketFailure | null;
}

export interface WarmTargetSetView {
  version: string;
  buckets: RolloutBucket[];
  /** staged only: a rollout of it is running right now. */
  inProgress: boolean;
}

export interface BackoffView {
  version: string;
  failures: number;
  lastFailureTs: number | null;
  nextAttemptTs: number;
  lastReason: string | null;
}

export interface RolloutView {
  phase: string;
  targetVersion: string | null;
  fromVersion: string | null;
  startedAtTs: number | null;
  phaseSinceTs: number | null;
  buckets: {
    done: number;
    /** Buckets whose last build failed (older runners: 0). */
    failed: number;
    total: number;
    current: string | null;
    buckets: RolloutBucket[];
  } | null;
}

export interface RunnerReadiness {
  kind: ReadinessKind;
  admission: AdmissionState;
  /** Short badge text: "accepting jobs", "rolling out", "starting", … */
  badge: string;
  badgeVariant: "success" | "info" | "warning";
  /** Why intake is closed (null while serving). */
  reason: string | null;
  /** Human rollout progress ("warming v1.2.3 — glibc, 2/4 warm"), or null. */
  progress: string | null;
  rollout: RolloutView | null;
  /** Staged version's buckets (running rollout, or a failed one backing
   *  off); null when nothing is staged or on an older runner. */
  staged: WarmTargetSetView | null;
  /** Failed-rollout backoff, or null. */
  backoff: BackoffView | null;
  /** "rollout of v1.2.4 failed 2× (glibc oom, macos-arm64 compile-error) —
   *  retry in 29m", or null. */
  backoffText: string | null;
  jobId: string | null;
  /** One-line summary for list rows. */
  line: string;
}

const ADMISSION: ReadonlySet<string> = new Set([
  "starting",
  "serving",
  "rolling-out",
  "degraded",
]);

const PHASE_TEXT: Record<string, string> = {
  draining: "draining the in-flight job",
  "teardown-check": "checking the previous job is torn down",
  resolving: "resolving the release",
  installing: "installing",
  "building-image": "building the transpiler image for",
  warming: "warming",
  swapping: "swapping in",
  verifying: "verifying the live pair",
};

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function str(v: unknown): string | null {
  return typeof v === "string" && v ? v : null;
}

const BUCKET_STATUS: ReadonlySet<string> = new Set([
  "warm",
  "prewarming",
  "pending",
  "failed",
]);

function readFailure(v: unknown): BucketFailure | null {
  if (!v || typeof v !== "object") return null;
  const f = v as Record<string, unknown>;
  const reason = str(f["reason"]);
  if (!reason) return null;
  return {
    version: str(f["version"]),
    ts: num(f["ts"]),
    exitCode: num(f["exitCode"]),
    reason,
    errorLines: Array.isArray(f["errorLines"])
      ? (f["errorLines"] as unknown[]).filter(
          (l): l is string => typeof l === "string",
        )
      : [],
  };
}

/** A runner warm-bucket list (`warmTargets`, `rollout.buckets.buckets`,
 *  `warmTargetSets.*.buckets`), validated. */
export function readBuckets(v: unknown): RolloutBucket[] {
  if (!Array.isArray(v)) return [];
  return v.flatMap((x): RolloutBucket[] => {
    const o = (x ?? {}) as Record<string, unknown>;
    const label = str(o["label"]);
    const status = o["status"];
    return label && typeof status === "string" && BUCKET_STATUS.has(status)
      ? [
          {
            label,
            status: status as BucketStatus,
            lastFailure: readFailure(o["lastFailure"]),
          },
        ]
      : [];
  });
}

export function readWarmTargetSet(v: unknown): WarmTargetSetView | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const version = str(o["version"]);
  if (!version) return null;
  return {
    version,
    buckets: readBuckets(o["buckets"]),
    inProgress: o["inProgress"] === true,
  };
}

export function readBackoff(v: unknown): BackoffView | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const version = str(o["version"]);
  const failures = num(o["failures"]);
  const nextAttemptTs = num(o["nextAttemptTs"]);
  if (!version || failures === null || nextAttemptTs === null) return null;
  return {
    version,
    failures,
    lastFailureTs: num(o["lastFailureTs"]),
    nextAttemptTs,
    lastReason: str(o["lastReason"]),
  };
}

/** "glibc oom, macos-arm64 compile-error" — the failed buckets, or "". */
export function failedBucketsText(buckets: RolloutBucket[]): string {
  return buckets
    .filter((b) => b.status === "failed" && b.lastFailure)
    .map((b) => `${b.label} ${b.lastFailure!.reason}`)
    .join(", ");
}

export function backoffText(
  b: BackoffView,
  staged: WarmTargetSetView | null,
  nowMs: number = Date.now(),
): string {
  const failed =
    staged && staged.version === b.version
      ? failedBucketsText(staged.buckets)
      : "";
  const wait = b.nextAttemptTs - nowMs;
  return (
    `rollout of v${b.version} failed${b.failures > 1 ? ` ${b.failures}×` : ""}` +
    (failed ? ` (${failed})` : "") +
    (wait > 0 ? ` — retry in ${fmtDuration(wait)}` : " — retry due")
  );
}

export function readRollout(v: unknown): RolloutView | null {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  const phase = str(r["phase"]);
  if (!phase) return null;
  const b = (r["buckets"] ?? null) as Record<string, unknown> | null;
  const list = readBuckets(b?.["buckets"]);
  const done = num(b?.["done"]);
  const total = num(b?.["total"]);
  return {
    phase,
    targetVersion: str(r["targetVersion"]),
    fromVersion: str(r["fromVersion"]),
    startedAtTs: num(r["startedAtTs"]),
    phaseSinceTs: num(r["phaseSinceTs"]),
    buckets:
      b && done !== null && total !== null
        ? {
            done,
            failed:
              num(b["failed"]) ??
              list.filter((x) => x.status === "failed").length,
            total,
            current: str(b["current"]),
            buckets: list,
          }
        : null,
  };
}

function fmtDuration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}m`;
}

export function rolloutProgressText(
  r: RolloutView,
  nowMs: number = Date.now(),
): string {
  const v = r.targetVersion ? `v${r.targetVersion}` : null;
  const verb = PHASE_TEXT[r.phase] ?? r.phase;
  const versioned = [
    "installing",
    "building-image",
    "warming",
    "swapping",
    "verifying",
  ].includes(r.phase);
  let text = versioned && v ? `${verb} ${v}` : verb;
  if (r.buckets && (r.phase === "warming" || r.phase === "verifying")) {
    const cur = r.buckets.current ? `${r.buckets.current}, ` : "";
    text += ` — ${cur}${r.buckets.done}/${r.buckets.total} targets warm`;
    if (r.buckets.failed > 0) text += `, ${r.buckets.failed} failed`;
  }
  if (r.fromVersion && v && r.fromVersion !== r.targetVersion)
    text += ` (from v${r.fromVersion})`;
  if (r.startedAtTs !== null)
    text += ` · ${fmtDuration(nowMs - r.startedAtTs)} in`;
  return text;
}

export function runnerReadiness(
  d: Record<string, unknown> | null | undefined,
  nowMs: number = Date.now(),
): RunnerReadiness | null {
  if (!d) return null;
  const a = (d["admission"] ?? null) as Record<string, unknown> | null;
  const state = a?.["state"];
  if (typeof state !== "string" || !ADMISSION.has(state)) return null;
  const admission = state as AdmissionState;
  const reason = str(a?.["reason"]);
  const rollout = readRollout(d["rollout"]);
  const sets = (d["warmTargetSets"] ?? null) as Record<string, unknown> | null;
  const staged = readWarmTargetSet(sets?.["staged"]);
  const backoff = readBackoff(d["rolloutBackoff"]);
  const bText = backoff ? backoffText(backoff, staged, nowMs) : null;
  const job = (d["job"] ?? null) as Record<string, unknown> | null;
  const jobId = str(job?.["jobId"]);
  const progress = rollout
    ? rolloutProgressText(rollout, nowMs)
    : // A runner with admission but no `rollout` field yet (or between two
      // back-to-back rollouts): occupancy still says whether warm-up runs.
      admission !== "serving" && d["occupancy"] === "prewarming"
      ? "prewarming"
      : null;

  if (admission === "serving") {
    const processing = d["occupancy"] === "processing";
    return {
      kind: processing ? "processing" : "accepting",
      admission,
      badge: "accepting jobs",
      badgeVariant: "success",
      reason: null,
      progress: null,
      rollout: null,
      staged,
      backoff,
      backoffText: bText,
      jobId,
      line:
        (processing
          ? jobId
            ? `accepting jobs · processing job ${jobId.slice(0, 8)}…`
            : "accepting jobs · processing a job"
          : "accepting jobs") + (bText ? ` · ${bText}` : ""),
    };
  }
  const kind: ReadinessKind = admission;
  const badge =
    admission === "rolling-out"
      ? "rolling out"
      : admission === "starting"
        ? "starting"
        : "degraded";
  // Rolling out / starting are expected, routine states (blue); degraded is
  // an incident with a reason (amber).
  const badgeVariant = admission === "degraded" ? "warning" : "info";
  const parts = [`${badge} — not accepting jobs`];
  if (progress) parts.push(progress);
  // The rolling-out reason is generic; with real progress it adds nothing.
  if (reason && !(admission === "rolling-out" && rollout)) parts.push(reason);
  if (bText) parts.push(bText);
  return {
    kind,
    admission,
    badge,
    badgeVariant,
    reason,
    progress,
    rollout,
    staged,
    backoff,
    backoffText: bText,
    jobId,
    line: parts.join(" · "),
  };
}
